import { createAction, createSlice } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';
import type { Dispatch } from 'redux';

import type { AppState, ListState } from './redux';
import { defaultPageSize } from './constants';
import type { VehicleModel } from '../lib/types';
import { vehicleModelApi } from '../lib/api';
import type { FetchListParams } from '../lib/api';
import type { VehicleModelsFilter } from '../lib/types/filters';

export type { VehicleModelsFilter } from '../lib/types/filters';

export const FETCH_VEHICLE_MODELS = 'vehicles/models/fetch';
export const DELETE_VEHICLE_MODEL = 'vehicles/models/delete';
export const SET_VEHICLE_MODELS_FILTERS = '/vehicles/models/filters/set';
export const CLEAN_VEHICLE_MODELS_FILTERS = '/vehicles/models/filters/clean';

export type VehicleModelsState = ListState<VehicleModel> & {
  filters: VehicleModelsFilter;
};

type VehicleModelsThunkDispatch = Dispatch & {
  <Result>(
    thunk: (dispatch: Dispatch, getState: () => AppState) => Result
  ): Result;
};

const initialState: VehicleModelsState = {
  data: [],
  page: 1,
  pageSize: defaultPageSize,
  totalCount: 0,
  filters: {}
};

const setVehicleModelsFiltersAction = createAction<VehicleModelsFilter>(
  SET_VEHICLE_MODELS_FILTERS
);
const cleanVehicleModelsFiltersAction = createAction(
  CLEAN_VEHICLE_MODELS_FILTERS
);

const vehicleModelsSlice = createSlice({
  name: 'vehicles/models',
  initialState,
  reducers: {
    fetch: (state, { payload }: PayloadAction<Partial<VehicleModelsState>>) => ({
      ...state,
      ...payload
    }),
    delete: (state, { payload }: PayloadAction<number>) => ({
      ...state,
      data: state.data.filter(model => model.id !== payload)
    })
  },
  extraReducers: builder => {
    builder
      .addCase(setVehicleModelsFiltersAction, (state, { payload }) => ({
        ...state,
        filters: payload
      }))
      .addCase(cleanVehicleModelsFiltersAction, state => ({
        ...state,
        filters: {}
      }));
  }
});

export const vehicleModelsActions = {
  ...vehicleModelsSlice.actions,
  setFilters: setVehicleModelsFiltersAction,
  cleanFilters: cleanVehicleModelsFiltersAction
};

export const fetchVehicleModels = (
  page: number = 1,
  params: FetchListParams<VehicleModelsFilter> = {}
) => async (dispatch: Dispatch, getState: () => AppState) => {
  const { filters, pageSize } = getState().vehicleModels;
  const { data, totalCount } = await vehicleModelApi.fetchVehicleModels({
    ...filters,
    page,
    pageSize,
    ...params
  });
  dispatch(
    vehicleModelsActions.fetch({
      data,
      totalCount,
      page,
      pageSize
    })
  );
};

export const applyFilters = (filters: VehicleModelsFilter) => async (
  dispatch: VehicleModelsThunkDispatch
) => {
  dispatch(vehicleModelsActions.setFilters(filters));
  dispatch(fetchVehicleModels());
};

export const cleanFilters = () => async (dispatch: VehicleModelsThunkDispatch) => {
  dispatch(vehicleModelsActions.cleanFilters());
  dispatch(fetchVehicleModels());
};

export default vehicleModelsSlice.reducer;
